import { useEffect, useState, useCallback } from "react";
import { ImageIcon, X, ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import { galleryImages } from "@/data/galleryImages";

const SPAN_CLASSES = {
  tall: "row-span-2",
  wide: "col-span-2",
  huge: "row-span-2 col-span-2",
};

export default function PhotoGallery() {
  const [lightbox, setLightbox] = useState(null); // index or null
  const total = galleryImages.length;

  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(
    () => setLightbox((i) => (i === null ? null : (i + 1) % total)),
    [total],
  );
  const prev = useCallback(
    () => setLightbox((i) => (i === null ? null : (i - 1 + total) % total)),
    [total],
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, close, next, prev]);

  return (
    <section
      id="gallery"
      data-testid="photo-gallery-section"
      className="section relative"
    >
      <div className="container-luxe">
        <div className="reveal flex items-end justify-between flex-wrap gap-6 max-w-4xl">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold-light">
              Photo Gallery · गैलरी
            </div>
            <h2
              data-testid="gallery-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] text-cream"
            >
              Moments of{" "}
              <span className="italic font-editorial gold-text">grace</span>.
            </h2>
            <p className="mt-5 text-cream-muted max-w-xl">
              A visual archive of ceremonies, gatherings and quiet
              consultations — curated from our official Instagram.
            </p>
          </div>
          <a
            href="#"
            data-testid="gallery-instagram-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full gold-border text-gold text-[11px] tracking-[0.24em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-500"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.5} />
            Follow on Instagram
          </a>
        </div>

        {total === 0 ? (
          <EmptyGallery />
        ) : (
          <div
            data-testid="gallery-grid"
            className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px]"
          >
            {galleryImages.map((img, i) => (
              <button
                type="button"
                key={img.src + i}
                onClick={() => setLightbox(i)}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden rounded-2xl gold-border bg-[#0A1428] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  SPAN_CLASSES[img.span] || ""
                }`}
                style={{ transitionDelay: `${i * 40}ms` }}
              >
                <img
                  src={img.src}
                  alt={img.alt || `Gallery ${i + 1}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain p-2 transition-transform duration-[900ms] group-hover:scale-105"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 30%, rgba(212,175,55,0.10), transparent 70%), #050B14",
                  }}
                />
                {/* Dark overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/95 via-[#050B14]/25 to-transparent opacity-70 group-hover:opacity-95 transition-opacity duration-500" />
                {/* Gold ring on hover */}
                <div className="absolute inset-2 rounded-xl border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/60 transition-colors duration-500" />
                {/* Caption */}
                <div className="absolute bottom-0 inset-x-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-gold">
                    Jay Ambe · Instagram
                  </div>
                  {img.alt && (
                    <div className="mt-1 font-editorial italic text-cream text-[15px] leading-snug line-clamp-2">
                      {img.alt}
                    </div>
                  )}
                </div>
                {/* Corner ornament */}
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center bg-[#050B14]/70 backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <ImageIcon className="w-3.5 h-3.5 text-gold" strokeWidth={1.6} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && galleryImages[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[80] bg-[#050B14]/95 backdrop-blur-xl grid place-items-center animate-in fade-in duration-300"
          onClick={close}
        >
          <button
            data-testid="lightbox-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-5 right-5 sm:top-8 sm:right-8 w-11 h-11 rounded-full gold-border grid place-items-center text-gold hover:bg-[#D4AF37] hover:text-[#050B14] transition-colors duration-500"
          >
            <X className="w-5 h-5" />
          </button>
          {total > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Previous"
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full gold-border grid place-items-center text-gold hover:bg-[#D4AF37] hover:text-[#050B14] transition-colors duration-500"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Next"
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full gold-border grid place-items-center text-gold hover:bg-[#D4AF37] hover:text-[#050B14] transition-colors duration-500"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <div
            className="relative max-w-[95vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[lightbox].src}
              alt={galleryImages[lightbox].alt || `Gallery ${lightbox + 1}`}
              className="max-w-[95vw] max-h-[75vh] object-contain rounded-lg gold-border"
              style={{
                boxShadow:
                  "0 0 0 1px rgba(212,175,55,0.35), 0 30px 80px -20px rgba(212,175,55,0.35)",
              }}
              data-testid="lightbox-image"
            />
            <div className="mt-5 text-center max-w-2xl">
              <div className="text-[10px] tracking-[0.32em] uppercase text-gold">
                {lightbox + 1} / {total}
              </div>
              {galleryImages[lightbox].alt && (
                <div className="mt-2 font-editorial italic text-cream text-lg">
                  {galleryImages[lightbox].alt}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EmptyGallery() {
  return (
    <div
      data-testid="gallery-empty-state"
      className="mt-14 glass-strong rounded-3xl p-10 sm:p-14 text-center"
    >
      <div className="w-16 h-16 mx-auto rounded-full gold-border grid place-items-center">
        <Instagram className="w-6 h-6 text-gold" strokeWidth={1.4} />
      </div>
      <h3 className="mt-6 font-display text-2xl text-cream">
        Instagram photos coming soon
      </h3>
      <p className="mt-3 font-devanagari text-gold-light/90">
        चित्र शीघ्र ही अपलोड किए जाएँगे
      </p>
      <p className="mt-4 max-w-lg mx-auto text-cream-muted text-[14px] leading-relaxed">
        Our official Instagram photographs will appear here once uploaded.
        Follow along for the latest ceremonies, gatherings and moments of
        grace from Jay Ambe Jyotish.
      </p>
    </div>
  );
}
